import React, { useState, useEffect } from "react";
import "./penaltytimingsettings.css"; 
import { useSelector, useDispatch } from "react-redux";
import SuccessOverlay from "../../UserComponents/overlays/SuccessOverlay.js";
import ManualTimeInput from "../../UserComponents/modals/ManualTimeInput.js"
import { editTeam, updateTeamSetting } from "../../services/team.services.js";
import { UpdateSelectedTeam } from "../../redux/actions/team.actions.js";
import UserSidebar from "../../../components/userSiderbar/userSidebar.jsx";

const PenaltyTimeSetting = (props) => {
  const dispatch = useDispatch();
  const operatorTeam = useSelector((state) => state.team.selectedTeam);
  const user = useSelector((state) => state.auth.user);

  const [initialFormState, setInitialFormState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [manualTimeModalVisible, setManualTimeModalVisible] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [savedManualTime, setSavedManualTime] = useState(null);
  const [editedManualTime, setEditedManualTime] = useState(null);

const normalizeTimeValue = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Handle string format like "MM:SS" or just seconds as string
      if (value.includes(':')) {
        const [minutes, seconds] = value.split(':').map(Number);
        return minutes * 60 + seconds;
      }
      return Number(value);
    }
    return 0; // Default fallback
  };

  useEffect(() => {
    if (operatorTeam?.teamId?.penalty_time_options) {
      // Convert all values to numbers and filter out invalid ones
      const normalizedOptions = operatorTeam.teamId.penalty_time_options
        .map(normalizeTimeValue)
        .filter(time => !isNaN(time) && time > 0);
      
      const sortedOptions = [...normalizedOptions].sort((a, b) => a - b);
      setInitialFormState(sortedOptions);
    }
  }, [operatorTeam?.teamId?.penalty_time_options]);

  const toggleSuccessModal = () => {
    setSuccessVisible(true);
    setTimeout(() => {
      setSuccessVisible(false);
    }, 2500);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Ensure we have a valid number
      const validSavedTime = savedManualTime !== null ? normalizeTimeValue(savedManualTime) : null;
      
      if (validSavedTime === null || isNaN(validSavedTime)) {
        console.error("Invalid time value");
        return;
      }
      
      const updatedOptions = [...initialFormState, validSavedTime].sort((a, b) => a - b);
      await editTeam(
        {
          teamId: operatorTeam.teamId._id,
          penalty_time_options: updatedOptions,
        },
        user?.token
      );
      toggleSuccessModal();
      dispatch(UpdateSelectedTeam(operatorTeam._id));
      setSavedManualTime(null);
      setInitialFormState(updatedOptions);
      setManualTimeModalVisible(false);
    } catch (e) {
      console.log("Error at Add New Time'  ===>", e.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (editIndex === null) return;

    try {
      setLoading(true);
      // Ensure we have a valid number
      const validEditedTime = editedManualTime !== null ? normalizeTimeValue(editedManualTime) : null;
      
      if (validEditedTime === null || isNaN(validEditedTime)) {
        console.error("Invalid time value");
        return;
      }
      
      const updatedOptions = [...initialFormState];
      updatedOptions[editIndex] = validEditedTime;
      const sortedOptions = updatedOptions.sort((a, b) => a - b);
      await editTeam(
        {
          teamId: operatorTeam.teamId._id,
          penalty_time_options: sortedOptions,
        },
        user?.token
      );
      toggleSuccessModal();
      dispatch(UpdateSelectedTeam(operatorTeam._id));
      setEditedManualTime(null);
      setEditIndex(null);
      setInitialFormState(sortedOptions);
    } catch (e) {
      console.log("Error at Edit Time'  ===>", e.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetToDefault = async () => {
    if (window.confirm("Are you sure you want to reset the Penalty Times to default?")) {
       try {
              setLoading(true);
              const settingName = 'penalty_time_options';
              const teamId = operatorTeam.teamId._id;
              const token = user?.token;
  
              const response = await updateTeamSetting(settingName, teamId, token);
              // console.log("RESET RESPONSE", response?.data);
              const updatedOptions = response?.data?.penalty_time_options;
              const values = updatedOptions.map(item => item.value);
              const sortedValues = values.sort((a, b) => a - b);
              
              setInitialFormState(sortedValues);
              toggleSuccessModal();
              dispatch(UpdateSelectedTeam(operatorTeam?._id));
            } catch (e) {
              console.log("Error at Reset Penalty Times:", e.response?.data || e.message);
            } finally {
              setLoading(false);
            }
    }
  };

  useEffect(() => {
    if (savedManualTime !== null) {
      handleSave();
    }
  }, [savedManualTime]);

  useEffect(() => {
    if (editedManualTime !== null) {
      handleEdit();
    }
  }, [editedManualTime]);

  const handleDelete = async (index) => {
    try {
      setLoading(true);
      const updatedOptions = [...initialFormState];
      updatedOptions.splice(index, 1);
      const sortedOptions = updatedOptions.sort((a, b) => a - b);
      await editTeam(
        {
          teamId: operatorTeam.teamId._id,
          penalty_time_options: sortedOptions,
        },
        user?.token
      );
      toggleSuccessModal();
      dispatch(UpdateSelectedTeam(operatorTeam?._id));
      setInitialFormState(sortedOptions);
    } catch (e) {
      console.log("Error at Delete Time'  ===>", e.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to format time in MM:SS format
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === null) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Debug function to check what's in the data
  const debugData = () => {
    console.log("Operator Team:", operatorTeam);
    console.log("Penalty Time Options:", operatorTeam?.teamId?.penalty_time_options);
    console.log("Initial Form State:", initialFormState);
  };

  return (
    <div className="container-pen-timing ">
      <UserSidebar />
      <div className="img-pen-timing">
        <h1 className='headerTxt-pen-timing'>PENALTY TIMES</h1>
      </div>

      {/* Debug button - you can remove this in production */}
      <button onClick={debugData} style={{position: 'absolute', top: '10px', right: '10px', zIndex: 1000}}>
        Debug Data
      </button>

      <div className="addBtnContainer-pen-timing">
        <button className="defaultBtn-pen-timing" onClick={handleResetToDefault}>Reset to Default</button>
        <button className="defaultBtn-pen-timing" onClick={() => setManualTimeModalVisible(true)}>
          + Add a Penalty Time 
        </button>
      </div>

      <ul className="turnoverView-pen-timing">
        {initialFormState.length > 0 ? (
          initialFormState.map((item, index) => (
            <li className="turnoverTxt-pen-timing" key={index}>
              <span className='time-pen'>{formatTime(item)}</span>
              <button className="editButton-pen-timing" onClick={() => {
                setEditIndex(index);
                setEditModalVisible(true);
              }}>Edit</button>
              <button className="deleteButton-pen-timing" onClick={() => handleDelete(index)}>Delete</button>
            </li>
          ))
        ) : (
          <li className="turnoverTxt-pen-timing" style={{fontWeight: '600'}}>No penalty times configured!</li>
        )}
      </ul>

      {manualTimeModalVisible && (
        <ManualTimeInput
          isVisible={manualTimeModalVisible}
          onBackdropPress={() => setManualTimeModalVisible(false)}
          onSave={setSavedManualTime}
          settings={true}
          heading="Add Penalty Time"
          onClose={() => {
            setManualTimeModalVisible(false);
          }}
        />
      )}

      {editModalVisible && (
        <ManualTimeInput
          isVisible={editModalVisible}
          onBackdropPress={() => setEditModalVisible(false)}
          onSave={setEditedManualTime}
          settings={true}
          initialValue={editIndex !== null ? initialFormState[editIndex] : 0}
          heading="Edit Penalty Time"
          onClose={() => {
            setEditModalVisible(false);
          }}
        />
      )}

      {successVisible && <SuccessOverlay message="Changes saved successfully!" />}

      {loading && <div className="loading-spinner">Loading...</div>}
    </div>
  );
};

export default PenaltyTimeSetting;